// assets/mirror-intake.js
// The Mirror's two-person intake gate. The birth-data-gate pattern, doubled: both people need a
// name, a birth date (adult), and a *selected* birthplace (real coordinates) before add-to-cart,
// and the both-people CONSENT checkbox is a hard gate — the add-to-cart button stays disabled
// until it is checked (required, never pre-checked). Birth times and pronouns are OPTIONAL and
// never gate anything. No-ops off the Mirror PDP (the element only renders on its template).
//
// Two layers, matching the store's existing gates:
//   1. consent unchecked -> the submit button is disabled (visible, explained inline)
//   2. submit attempted with anything else missing -> intercepted with a focused, warm hint
// The server re-checks everything (mirror-order intake fails closed; the generator refuses
// without --consent), so this is UX, not the guarantee.

const ADULT_FLOOR = 18; // mirrors reading/preflight.mjs AGE_BANDS.FULL_FLOOR
const SKY_STORY_URL = '/pages/your-sky-story';

class MirrorIntake extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    const formId = this.dataset.formId;
    this.form =
      (formId && document.getElementById(formId)) ||
      document.querySelector('form[data-type="add-to-cart-form"]');
    this.button = /** @type {HTMLButtonElement | null} */ (
      this.form ? this.form.querySelector('button[type="submit"][name="add"]') : null
    );
    this.hint = /** @type {HTMLElement | null} */ (this.querySelector('[data-mirror-hint]'));
    this.consentEl = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-mirror-consent]'));
    this.consentNote = /** @type {HTMLElement | null} */ (this.querySelector('[data-mirror-consent-note]'));
    // A button already disabled at mount is sold out / unavailable — never re-enable it here.
    this.unavailable = Boolean(this.button && this.button.disabled);

    this.form?.addEventListener('submit', (e) => this.onSubmit(e), true);
    document.addEventListener('input', () => this.update());
    document.addEventListener('change', () => this.update());
    this.update();
  }

  /** @param {string} name */
  field(name) {
    const el = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`[name="properties[${name}]"]`)
    );
    return el ? el.value.trim() : '';
  }

  /**
   * 'ok' | 'future' | 'minor' | 'invalid' for a YYYY-MM-DD date-input value.
   * @param {string} value
   */
  dateStatus(value) {
    const born = new Date(`${value}T12:00:00`);
    if (Number.isNaN(born.getTime())) return 'invalid';
    const now = new Date();
    if (born > now) return 'future';
    const age = Math.floor((now.getTime() - born.getTime()) / (365.2425 * 864e5));
    return age < ADULT_FLOOR ? 'minor' : 'ok';
  }

  /** First problem as {message, html?, focus} or null when everything required is present. */
  firstMissing() {
    for (const [p, who] of [['A', 'the first person'], ['B', 'the second person']]) {
      if (!this.field(`${p} Name`)) {
        return { message: `Add the name ${who} goes by to continue.`, focus: `[name="properties[${p} Name]"]` };
      }
      const date = this.field(`${p} Birth Date`);
      if (!date) {
        return { message: `Add ${who}'s birth date to continue.`, focus: `[name="properties[${p} Birth Date]"]` };
      }
      const status = this.dateStatus(date);
      if (status === 'future' || status === 'invalid') {
        return { message: `That birth date for ${who} does not look right. Please double-check it.`, focus: `[name="properties[${p} Birth Date]"]` };
      }
      if (status === 'minor') {
        return {
          message: 'The Mirror is written for two adults.',
          html: `The Mirror is written for two adults. For a child's sky, <a href="${SKY_STORY_URL}">Your Sky Story</a> is the keepsake made for them.`,
          focus: `[name="properties[${p} Birth Date]"]`,
        };
      }
      if (!this.field(`_${p} Lat`) || !this.field(`_${p} Lng`)) {
        return { message: `Search and select ${who}'s birthplace from the list to continue.`, focus: `[name="properties[${p} Birth Place]"]` };
      }
    }
    if (!(this.consentEl && this.consentEl.checked)) {
      return { message: 'Check the permission box to continue.', focus: '[data-mirror-consent]' };
    }
    return null;
  }

  update() {
    const consented = Boolean(this.consentEl && this.consentEl.checked);
    // The consent checkbox is the hard gate on the button itself (required, never pre-checked).
    if (this.button && !this.unavailable) {
      this.button.disabled = !consented;
      this.button.setAttribute('aria-disabled', String(!consented));
    }
    if (this.consentNote) this.consentNote.hidden = consented;
    if (this.hint && !this.firstMissing()) this.hint.hidden = true;
  }

  /** @param {SubmitEvent} e */
  onSubmit(e) {
    const missing = this.firstMissing();
    if (!missing) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (this.hint) {
      if (missing.html) this.hint.innerHTML = missing.html;
      else this.hint.textContent = missing.message;
      this.hint.hidden = false;
    }
    const el = missing.focus && /** @type {HTMLElement | null} */ (document.querySelector(missing.focus));
    if (!el) return;
    const target = el.offsetParent === null
      ? ((el.closest('stz-time-picker, .mirror-intake__field') || el.parentElement)?.querySelector('select, input:not([type="hidden"]), textarea, button') || el)
      : el;
    /** @type {HTMLElement} */ (target).focus();
  }
}

if (!customElements.get('mirror-intake')) {
  customElements.define('mirror-intake', MirrorIntake);
}
